import React, { useContext, useEffect, useRef, useState } from "react";
import { useSessionStorage } from "usehooks-ts";
import { getBannerTextColor } from "../../utils/publicBannerColors";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Icon } from "../Common/Icons/LucideIcons";
import {
  BannerContainer,
  BannerContent,
  BannerWrapper,
  DismissButton,
  ReadMoreButton,
} from "./__styles__/PublicBanner";

interface SessionState {
  publicBannerDismissed: boolean;
}

export const PublicBanner = () => {
  const { account } = useContext(AuthContext);
  const { bannerContent, bannerColor, bannerDisplayed } =
    account?.publicPortal || {};

  const [sessionState, setSessionState] = useSessionStorage<SessionState>(
    "account",
    { publicBannerDismissed: false }
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleDismiss = () => {
    setSessionState({
      ...sessionState,
      publicBannerDismissed: true,
    });
  };

  const checkOverflow = () => {
    const element = wrapperRef.current?.firstElementChild;
    if (!element) return;

    setHasOverflow(element.scrollHeight > element.clientHeight);
  };

  const trackReadMoreClicked = () => {
    track("Public Website Banner Read More Clicked", {
      "Banner Color": bannerColor,
      "Banner Content": bannerContent,
    });
  };

  useEffect(() => {
    checkOverflow();
  }, [bannerContent]);

  useEffect(() => {
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  if (!bannerDisplayed || sessionState.publicBannerDismissed) {
    return null;
  }

  const textColor = getBannerTextColor(bannerColor || "");
  const iconColor =
    textColor === "white" ? "contentPrimaryInverted" : "contentPrimary";

  return (
    <BannerContainer
      css={{ backgroundColor: bannerColor || "", color: textColor }}
      textColor={textColor}
      role="banner"
    >
      <BannerWrapper ref={wrapperRef}>
        <BannerContent
          markdown={bannerContent || ""}
          isExpanded={isExpanded}
          options={{ simpleLineBreaks: true }}
        />
        <DismissButton
          size="xSmall"
          styleVariant="ghost"
          onClick={handleDismiss}
          aria-label="Close banner"
        >
          <Icon iconName="x" color={iconColor} size="16px" />
        </DismissButton>
      </BannerWrapper>
      {!isExpanded && hasOverflow && (
        <ReadMoreButton
          onClick={() => {
            trackReadMoreClicked();
            setIsExpanded(true);
          }}
          textColor={textColor}
          aria-label="Read more banner content"
        >
          Read more
        </ReadMoreButton>
      )}
    </BannerContainer>
  );
};
